import type { StackFrameType } from '@cotera/api';
import { Badge } from '@cotera/client/v0/components/ui/badge';
import { Button } from '@cotera/client/v0/components/ui/button';
import { ShimmerText } from '@cotera/client/v0/components/ui/shimmer-text';
import { DG } from '@cotera/sdk/core';
import { Assert } from '@cotera/utilities';
import type { StackFramesWatchableValue } from './cell.model';
import { hasWarehouseValue } from './cell.model';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useActiveTab, useTabManager, useTabParams } from '../../tab-manager';
import { DefaultCell } from '../cell';
import { CellComponentPropsWithModel } from '../types';
import { useAtcCellModel } from './context';
import { AtcCellInViewGate } from './atc-cell-in-view-gate';
import {
  EXTRACT_OBJECT_VERBS,
  PENDING_VERBS,
  RETRY_VERBS,
  STEP_CHAT_VERBS,
} from './constants';

export { hasWarehouseValue } from './cell.model';

export function useRotatingVerb(
  verbs: string[],
  resetKey: string | undefined,
  intervalMs: number = 5000,
  jitterMs: number = 1500
): string {
  const [index, setIndex] = useState(() =>
    Math.floor(Math.random() * verbs.length)
  );

  // Immediately pick a new verb when resetKey changes (e.g., new frame)
  useEffect(() => {
    setIndex(Math.floor(Math.random() * verbs.length));
  }, [resetKey, verbs.length]);

  // Rotate on interval with jitter for more natural feel
  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>;

    const scheduleNext = () => {
      const jitter = (Math.random() - 0.5) * 2 * jitterMs; // -jitterMs to +jitterMs
      const delay = Math.max(intervalMs + jitter, 1000); // minimum 1s delay
      timerId = setTimeout(() => {
        setIndex(Math.floor(Math.random() * verbs.length));
        scheduleNext();
      }, delay);
    };

    scheduleNext();
    return () => clearTimeout(timerId);
  }, [verbs, intervalMs, jitterMs]);

  return verbs[index]!;
}

export function getCurrentExecutingFrame(
  frames: StackFrameType[]
): StackFrameType | null {
  const activeFrames = frames.filter(
    (f) =>
      f.status === 'running' ||
      f.status === 'pending' ||
      f.status === 'waiting' ||
      f.status === 'allocated' ||
      f.status === 'queued'
  );

  if (activeFrames.length === 0) {
    return null;
  }

  // Return deepest active frame (current leaf of execution)
  return activeFrames.reduce((deepest, current) =>
    current.depth > deepest.depth ||
    (current.depth === deepest.depth && current.position > deepest.position)
      ? current
      : deepest
  );
}

export const DynamicStatusText: React.FC<{ frames: StackFrameType[] }> = ({
  frames,
}) => {
  const currentFrame = getCurrentExecutingFrame(frames);
  const rootFrame = frames[0];

  // Use current frame's ID as reset key so verbs update immediately when frame changes
  const frameKey = currentFrame?.id;

  // Use hooks unconditionally at top level
  const stepChatVerb = useRotatingVerb(STEP_CHAT_VERBS, frameKey);
  const extractVerb = useRotatingVerb(EXTRACT_OBJECT_VERBS, frameKey);
  const pendingVerb = useRotatingVerb(PENDING_VERBS, frameKey);
  const retryVerb = useRotatingVerb(RETRY_VERBS, frameKey);

  if (!rootFrame) {
    return null;
  }

  // Check root frame waiting status first (recovering from error)
  // This must come before the currentFrame null check since there may be
  // no active child frames during recovery
  if (rootFrame.status === 'waiting') {
    return <ShimmerText variant="warning">{retryVerb}...</ShimmerText>;
  }

  if (!currentFrame) {
    return null;
  }

  if (currentFrame.status === 'pending') {
    return <ShimmerText variant="muted">{pendingVerb}...</ShimmerText>;
  }

  if (currentFrame.status === 'queued') {
    return <ShimmerText variant="muted">Queued...</ShimmerText>;
  }

  if (currentFrame.status === 'waiting') {
    return <ShimmerText variant="warning">{retryVerb}...</ShimmerText>;
  }

  if (currentFrame.status === 'running') {
    switch (currentFrame.fpType) {
      case 'tool':
        return (
          <ShimmerText variant="muted">
            Running {currentFrame.displayName}...
          </ShimmerText>
        );
      case 'stepChat':
      case 'agent':
        return <ShimmerText variant="muted">{stepChatVerb}...</ShimmerText>;
      case 'extractObject':
        return <ShimmerText variant="muted">{extractVerb}...</ShimmerText>;
      default:
        return (
          <ShimmerText variant="muted">
            Running {currentFrame.displayName}...
          </ShimmerText>
        );
    }
  }

  return null;
};

// Wrapper component that adds hover play button and click-to-detail
// Does NOT render DefaultCell - children should provide their own cell styling
export const AtcCellWrapper: React.FC<{
  runKey: string;
  frameId: string | undefined;
  children: React.ReactNode;
  onPlay: () => void;
}> = ({ runKey, frameId, children, onPlay }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { push } = useTabManager();

  const handlePlay = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onPlay();
    },
    [onPlay]
  );

  const handleDetailView = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      // The run detail page only needs the runKey (it derives the root frame
      // itself); the frameKey segment is optional. Cells that already have a
      // persisted warehouse value skip frame polling, so frameId is undefined —
      // still navigate to the run using just the runKey.
      push({
        route:
          frameId !== undefined
            ? `/run/${runKey}/${frameId}`
            : `/run/${runKey}`,
      });
    },
    [push, runKey, frameId]
  );

  return (
    <div
      className="relative h-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {isHovered && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1 z-10">
          <Button
            size="icon-sm"
            variant="outline"
            icon="activity"
            onClick={handleDetailView}
          />
          <Button
            size="icon-sm"
            variant="outline"
            icon="play"
            onClick={handlePlay}
          />
        </div>
      )}
    </div>
  );
};

export const AtcSuspensefulCellComponentForRunKey = (
  baseProps: CellComponentPropsWithModel & {
    startingIdentity: DG.Graph.Identity;
    currentIdentity: DG.Graph.Identity;
    runKey: string;
    cellRenderer: React.ComponentType<CellComponentPropsWithModel>;
    onPlay: () => void;

    /** If fetched data is null, whether to render as a waiting shimmer. */
    shimmerIfNull?: boolean;

    /** When true and data is null (fetch was skipped), show value or "Not Run" instead of Loading. */
    skippedInitialFetch?: boolean;
  }
) => {
  const handleMouseOver = useCallback(() => {
    baseProps.model.columns.set(
      baseProps.model.columns
        .snapshot()
        .map((x) =>
          x.key === baseProps.column.key ? { ...x, inView: true } : x
        )
    );
  }, [baseProps.model.columns, baseProps.column.key]);

  // Memoize props to prevent creating new object on every render
  const props = useMemo(
    () => ({ ...baseProps, onMouseOver: handleMouseOver }),
    [baseProps, handleMouseOver]
  );

  const currentIdentity = props.currentIdentity;

  const activeTab = useActiveTab();
  const { tabId } = useTabParams();
  const cellModel = useAtcCellModel();
  const pollingEnabled = activeTab === tabId;

  // Use subscribe + useState instead of useWatchableValue to ensure we re-render
  // when the PollingWatchable updates asynchronously (jotai useAtom can miss updates
  // from promise callbacks in some cases)
  const [data, setData] = useState<StackFramesWatchableValue>(() =>
    cellModel.stackFramesWatchable.snapshot()
  );
  useEffect(() => {
    return cellModel.stackFramesWatchable.subscribe(setData);
  }, [cellModel.stackFramesWatchable]);

  // Optimistic pending state - shows shimmer immediately after clicking play
  const [playClicked, setPlayClicked] = useState(false);

  const onPlay = props.onPlay;
  const handlePlayWithRefetch = useCallback(() => {
    setPlayClicked(true);
    onPlay();
    // Delay to allow backend to create run before we start polling
    setTimeout(() => {
      cellModel.restartStackFramesPolling();
    }, 2000);
  }, [onPlay, cellModel]);

  const renderCell = (): React.ReactNode => {
    // Initial loading - watchable hasn't received first poll result yet.
    // Prefer the warehouse row value when present.
    if (data === null) {
      if (hasWarehouseValue(props.value)) {
        return (
          <AtcCellWrapper
            runKey={props.runKey}
            frameId={undefined}
            onPlay={handlePlayWithRefetch}
          >
            <props.cellRenderer {...props} />
          </AtcCellWrapper>
        );
      }
      if (props.skippedInitialFetch) {
        return (
          <AtcCellWrapper
            runKey={props.runKey}
            frameId={undefined}
            onPlay={handlePlayWithRefetch}
          >
            <DefaultCell {...props}>
              <Badge variant="muted">Not Run</Badge>
            </DefaultCell>
          </AtcCellWrapper>
        );
      }
      return (
        <AtcCellWrapper
          runKey={props.runKey}
          frameId={undefined}
          onPlay={handlePlayWithRefetch}
        >
          <DefaultCell {...props}>
            <ShimmerText variant="muted">Loading...</ShimmerText>
          </DefaultCell>
        </AtcCellWrapper>
      );
    }

    if (data.isErr()) {
      if (hasWarehouseValue(props.value)) {
        return (
          <AtcCellWrapper
            runKey={props.runKey}
            frameId={undefined}
            onPlay={handlePlayWithRefetch}
          >
            <props.cellRenderer {...props} />
          </AtcCellWrapper>
        );
      }
      return (
        <AtcCellWrapper
          runKey={props.runKey}
          frameId={undefined}
          onPlay={handlePlayWithRefetch}
        >
          <DefaultCell {...props}>
            <Badge variant={'destructive'}>Error</Badge>
          </DefaultCell>
        </AtcCellWrapper>
      );
    }

    if (data.value === null) {
      /**
       * There are three cases here:
       *
       * 1. Cell Play button or Run on New Rows (via shimmerIfNull) has been clicked
       *  - Render waiting shimmer (to show a response between the lag until data.value
       *    populates with an initial waiting status).
       *
       * 2. props.startingIdentity equal currentIdentity
       *  - A new column run has not been invoked this session => render previous data (props.value) if it exists
       *
       * 3. props.startingIdentity not equal currentIdentity
       *  - A new column run has been invoked this session (but new data not yet available) => render waiting shimmer
       *  - Don't show existing values as they will be stale
       */

      if (playClicked || props.shimmerIfNull) {
        return (
          <AtcCellWrapper
            runKey={props.runKey}
            frameId={undefined}
            onPlay={handlePlayWithRefetch}
          >
            <DefaultCell {...props}>
              <ShimmerText variant="muted">Waiting...</ShimmerText>
            </DefaultCell>
          </AtcCellWrapper>
        );
      }

      if (props.startingIdentity.eq(currentIdentity)) {
        return (
          <AtcCellWrapper
            runKey={props.runKey}
            frameId={undefined}
            onPlay={handlePlayWithRefetch}
          >
            {hasWarehouseValue(props.value) ? (
              <props.cellRenderer {...props} />
            ) : (
              <DefaultCell {...props}>
                <Badge variant="muted">Not Run</Badge>
              </DefaultCell>
            )}
          </AtcCellWrapper>
        );
      } else {
        return (
          <AtcCellWrapper
            runKey={props.runKey}
            frameId={undefined}
            onPlay={handlePlayWithRefetch}
          >
            <DefaultCell {...props}>
              <ShimmerText variant="muted">Waiting...</ShimmerText>
            </DefaultCell>
          </AtcCellWrapper>
        );
      }
    }

    Assert.assert(data.value !== null);
    // Get the root frame from the response
    const rootFrame = data.value.frames[0];
    if (!rootFrame) {
      return (
        <AtcCellWrapper
          runKey={props.runKey}
          frameId={undefined}
          onPlay={handlePlayWithRefetch}
        >
          <props.cellRenderer {...props} />
        </AtcCellWrapper>
      );
    }

    const frameId = rootFrame.id;

    if (rootFrame.status === 'failed' || rootFrame.status === 'stalled') {
      return (
        <AtcCellWrapper
          runKey={props.runKey}
          frameId={frameId}
          onPlay={handlePlayWithRefetch}
        >
          <DefaultCell {...props}>
            <Badge variant={'destructive'}>Failed</Badge>
          </DefaultCell>
        </AtcCellWrapper>
      );
    }

    if (rootFrame.status === 'cancelled') {
      return (
        <AtcCellWrapper
          runKey={props.runKey}
          frameId={frameId}
          onPlay={handlePlayWithRefetch}
        >
          <DefaultCell {...props}>
            <Badge variant={'gray'}>Cancelled</Badge>
          </DefaultCell>
        </AtcCellWrapper>
      );
    }

    if (rootFrame.status === 'completed') {
      // Safe to access .val as atc outputs are guaranteed to have shape {ty: string; val: any}
      const { output } = rootFrame;
      const value = output === null ? null : output['val'];
      return (
        <AtcCellWrapper
          runKey={props.runKey}
          frameId={frameId}
          onPlay={handlePlayWithRefetch}
        >
          <props.cellRenderer {...props} value={value} />
        </AtcCellWrapper>
      );
    }

    // In-progress states - use dynamic status text with shimmer
    if (
      rootFrame.status === 'pending' ||
      rootFrame.status === 'running' ||
      rootFrame.status === 'waiting' ||
      rootFrame.status === 'allocated' ||
      rootFrame.status === 'queued'
    ) {
      return (
        <AtcCellWrapper
          runKey={props.runKey}
          frameId={frameId}
          onPlay={handlePlayWithRefetch}
        >
          <DefaultCell {...props}>
            <DynamicStatusText frames={data.value.frames} />
          </DefaultCell>
        </AtcCellWrapper>
      );
    }

    return (
      <AtcCellWrapper
        runKey={props.runKey}
        frameId={undefined}
        onPlay={handlePlayWithRefetch}
      >
        <props.cellRenderer {...props} />
      </AtcCellWrapper>
    );
  };

  return (
    <AtcCellInViewGate cellModel={cellModel} pollingEnabled={pollingEnabled}>
      {renderCell()}
    </AtcCellInViewGate>
  );
};
